import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError, type Attempt, type AttemptStatus, type PaperQuestion, type SubmitResult, type UnitDetail } from '../api';

type Answers = Record<string, any>;

export function TestPlayer(props: { unit: UnitDetail; onFinished: () => void }) {
  return <TestSession key={`${props.unit.courseId}:${props.unit.id}`} {...props} />;
}

function TestSession({ unit, onFinished }: { unit: UnitDetail; onFinished: () => void }) {
  const [attempt, setAttempt] = useState<Attempt | null>(null);
  const [answers, setAnswers] = useState<Answers>({});
  const [result, setResult] = useState<SubmitResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<AttemptStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [waitSeconds, setWaitSeconds] = useState(0);
  const mounted = useRef(true);
  const acting = useRef(false);
  const statusRequest = useRef(0);
  const statusReceivedAt = useRef(0);
  const refreshedDeadlines = useRef(new Set<string>());
  const isSurvey = unit.type === 'survey';
  const attemptsPath = `/courses/${unit.courseId}/units/${unit.id}/attempts`;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; statusRequest.current += 1; };
  }, []);

  const loadStatus = useCallback(async () => {
    if (isSurvey) return;
    const request = ++statusRequest.current;
    if (mounted.current) { setStatusLoading(true); setStatusError(null); }
    try {
      const next = await api.get<AttemptStatus>(attemptsPath);
      if (!mounted.current || request !== statusRequest.current) return;
      statusReceivedAt.current = performance.now();
      setStatus(next);
      setWaitSeconds(Math.max(0, Math.ceil(next.retryAfterSeconds)));
    } catch (e) {
      if (mounted.current && request === statusRequest.current) setStatusError((e as Error).message);
    } finally {
      if (mounted.current && request === statusRequest.current) setStatusLoading(false);
    }
  }, [attemptsPath, isSurvey]);

  useEffect(() => { void loadStatus(); }, [loadStatus]);

  // Use the server's remaining duration, so the learner's computer clock cannot shorten the wait.
  useEffect(() => {
    if (!status?.availableAt || status.canStart || status.canResume) return;
    const tick = () => {
      const remaining = Math.max(0, Math.ceil(status.retryAfterSeconds - (performance.now() - statusReceivedAt.current) / 1000));
      setWaitSeconds(remaining);
      if (remaining === 0 && !refreshedDeadlines.current.has(status.availableAt!)) {
        refreshedDeadlines.current.add(status.availableAt!);
        void loadStatus();
      }
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [status, loadStatus]);

  const start = async () => {
    if (acting.current) return;
    acting.current = true;
    setError(null); setBusy(true);
    try {
      const next = await api.post<Attempt>(attemptsPath);
      if (!mounted.current) return;
      setAttempt(next); setAnswers(next.answers ?? {}); setResult(null);
    } catch (e) {
      if (!mounted.current) return;
      const failure = e as ApiError;
      setError(failure.message);
      if (failure.status === 403 && failure.body?.availableAt) {
        statusReceivedAt.current = performance.now();
        setStatus(previous => previous ? {
          ...previous, canStart: false, canResume: false, reason: failure.message,
          availableAt: failure.body.availableAt, retryAfterSeconds: Number(failure.body.retryAfterSeconds) || 0,
        } : previous);
      }
      void loadStatus();
    } finally {
      acting.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  const submit = async () => {
    if (!attempt || acting.current) return;
    acting.current = true;
    setBusy(true); setError(null);
    try {
      const next = await api.post<SubmitResult>(`${attemptsPath}/${attempt.id}/submit`, { answers });
      if (!mounted.current) return;
      setResult(next); setAttempt(null); void loadStatus(); onFinished();
    } catch (e) {
      if (mounted.current) setError((e as Error).message);
    } finally {
      acting.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  const loadResult = async (attemptId: number) => {
    if (acting.current) return;
    acting.current = true;
    setBusy(true); setError(null);
    try {
      const next = await api.get<SubmitResult>(`${attemptsPath}/${attemptId}/results`);
      if (!mounted.current) return;
      setResult(next); void loadStatus();
    } catch (e) {
      if (mounted.current) setError((e as Error).message);
    } finally {
      acting.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  const availability = !isSurvey && <AttemptAvailability status={status} loading={statusLoading} error={statusError}
    waitSeconds={waitSeconds} busy={busy} onStart={() => void start()} onRefresh={() => void loadStatus()} />;

  if (result) return <>
    <Results result={result} isSurvey={isSurvey} busy={busy} onRefresh={() => void loadResult(result.attemptId)} />
    {error && <p className="auth__error" role="alert">{error}</p>}
    {availability}
    {!isSurvey && <button className="btn" disabled={busy} onClick={() => { setResult(null); setError(null); }}>Back to attempts</button>}
  </>;

  if (!attempt) {
    return (
      <div className="test-intro">
        {unit.contentHtml && <div className="prose" dangerouslySetInnerHTML={{ __html: unit.contentHtml }} />}
        <dl className="facts">
          <div><dt>Questions</dt><dd>{unit.questionCount ?? 0}</dd></div>
          {!isSurvey && <div><dt>Pass mark</dt><dd>{unit.testInfo?.passMark ?? 50}%</dd></div>}
          {unit.testInfo?.maxTimeSeconds ? <div><dt>Time limit</dt><dd>{Math.round(unit.testInfo.maxTimeSeconds / 60)} min</dd></div> : null}
          {unit.testInfo?.attemptsLimit ? <div><dt>Total attempts allowed</dt><dd>{unit.testInfo.attemptsLimit}</dd></div> : null}
        </dl>
        {!isSurvey && unit.testInfo?.questionsPerAttempt != null && <p className="muted">
          Each new attempt selects {unit.testInfo.questionsPerAttempt} question{unit.testInfo.questionsPerAttempt === 1 ? '' : 's'} at random from {unit.testInfo.questionPoolSize} available.
          {' '}Resuming an attempt keeps the same questions.
        </p>}
        {error && <p className="auth__error" role="alert">{error}</p>}
        {isSurvey ? <button className="btn btn--primary" onClick={() => void start()} disabled={busy}>
          {busy ? 'Opening…' : 'Start survey'}
        </button> : availability}
        {!isSurvey && status && status.attempts.length > 0 && <section aria-label="Previous attempts">
          <h2 className="h2">Your attempts</h2>
          <ul className="list">
            {status.attempts.map(previous => <li key={previous.id}>
              <p><strong>Attempt {previous.attemptNo}</strong> · {previous.submittedAt ? `Submitted ${localDate(previous.submittedAt)}` : `Started ${localDate(previous.startedAt)}`}</p>
              <p>{!previous.submittedAt ? 'In progress' : previous.needsReview ? 'Awaiting instructor marking' : `${previous.score ?? 0}% · ${previous.passed ? 'Passed' : 'Not passed'}`}</p>
              {previous.submittedAt && <button className="btn" disabled={busy} onClick={() => void loadResult(previous.id)}>
                {previous.needsReview ? 'Refresh marks and view results' : 'View results'}
              </button>}
            </li>)}
          </ul>
        </section>}
      </div>
    );
  }

  return <Paper key={attempt.id} attempt={attempt} answers={answers} setAnswers={setAnswers}
    onSubmit={submit} busy={busy} error={error} isSurvey={isSurvey} courseId={unit.courseId} unitId={unit.id} />;
}

function localDate(value: string): string {
  // Database timestamps without an offset are UTC, not the browser's local timezone.
  const normalized = value.replace(' ', 'T');
  const withOffset = /T.*(?:Z|[+-]\d{2}(?::?\d{2})?)$/i.test(normalized) ? normalized : `${normalized}Z`;
  const date = new Date(withOffset);
  return Number.isNaN(date.getTime()) ? 'time unavailable' : date.toLocaleString();
}

function waitDuration(seconds: number): string {
  const minutes = Math.ceil(seconds / 60);
  if (seconds < 60) return `${seconds} second${seconds === 1 ? '' : 's'}`;
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return `${hours} hour${hours === 1 ? '' : 's'}${rest ? ` ${rest} min` : ''}`;
}

function AttemptAvailability({ status, loading, error, waitSeconds, busy, onStart, onRefresh }: {
  status: AttemptStatus | null; loading: boolean; error: string | null; waitSeconds: number;
  busy: boolean; onStart: () => void; onRefresh: () => void;
}) {
  const canOpen = !!status && (status.canResume || status.canStart);
  return <section aria-label="Attempt availability" style={{ marginBlock: 20 }}>
    {status && <p className="muted">{status.attemptsRemaining == null ? 'No overall attempt limit.' : `${status.attemptsRemaining} new attempt${status.attemptsRemaining === 1 ? '' : 's'} remaining.`}</p>}
    {status?.reason && !canOpen && <p>{status.reason}</p>}
    {status?.availableAt && !canOpen && <p>Next attempt available: <time dateTime={status.availableAt}>{localDate(status.availableAt)}</time>
      {waitSeconds > 0 ? ` (in ${waitDuration(waitSeconds)}).` : '.'}</p>}
    {error && <p className="auth__error" role="alert">{error}</p>}
    {!status && loading && <p role="status">Checking attempt availability…</p>}
    <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
      <button className="btn btn--primary" onClick={onStart} disabled={busy || loading || !canOpen}>
        {busy ? 'Please wait…' : status?.canResume ? 'Resume test' : status && status.attempts.length > 0 ? 'Try again' : 'Start test'}
      </button>
      <button className="btn" onClick={onRefresh} disabled={busy || loading}>{loading ? 'Checking…' : 'Refresh availability'}</button>
    </div>
  </section>;
}

function Paper({
  attempt, answers, setAnswers, onSubmit, busy, error, isSurvey, courseId, unitId,
}: {
  attempt: Attempt; answers: Answers; setAnswers: (a: Answers) => void;
  onSubmit: () => void; busy: boolean; error: string | null; isSurvey: boolean;
  courseId: number; unitId: number;
}) {
  const [left, setLeft] = useState<number | null>(null);
  const submitted = useRef(false);
  const saves = useRef(Promise.resolve());
  const [saveError, setSaveError] = useState<string | null>(null);

  // Countdown, driven by the server's deadline rather than the clock we started.
  useEffect(() => {
    if (!attempt.deadlineAt) return;
    const end = new Date(attempt.deadlineAt).getTime();
    const tick = () => {
      const secs = Math.max(0, Math.round((end - Date.now()) / 1000));
      setLeft(secs);
      if (secs === 0 && !submitted.current) { submitted.current = true; onSubmit(); }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [attempt.deadlineAt, onSubmit]);

  // Autosave, so a dropped connection does not lose the paper.
  const save = useCallback((next: Answers) => {
    saves.current = saves.current.then(async () => { await api.patch(`/courses/${courseId}/units/${unitId}/attempts/${attempt.id}`, { answers: next }); setSaveError(null); }).catch(() => setSaveError('Answers could not be saved. Keep this page open and submit again when connected.'));
  }, [attempt.id, courseId, unitId]);

  const setAnswer = (qid: number, value: any) => {
    const next = { ...answers, [qid]: value };
    setAnswers(next);
    save({ [qid]: value });
  };

  const answered = attempt.questions.filter((q) => answers[q.id] != null).length;

  return (
    <div className="paper">
      {saveError && <p role="alert">{saveError}</p>}
      <div className="paper__bar">
        <span>{answered} of {attempt.questions.length} answered</span>
        {left != null && <span className={`paper__clock ${left < 60 ? 'is-low' : ''}`}>{fmt(left)}</span>}
      </div>

      {attempt.questions.map((q, i) => (
        <fieldset className="q" key={q.id}>
          <legend><span className="q__n">{i + 1}</span></legend>
          <div className="prose" dangerouslySetInnerHTML={{ __html: q.textHtml }} />
          <QuestionInput q={q} value={answers[q.id]} onChange={(v) => { if (!busy && left !== 0) setAnswer(q.id, v); }} />
        </fieldset>
      ))}

      {error && <p className="auth__error" role="alert">{error}</p>}
      <button className="btn btn--primary" onClick={onSubmit} disabled={busy}>
        {busy ? 'Submitting…' : isSurvey ? 'Submit answers' : 'Submit test'}
      </button>
    </div>
  );
}

const fmt = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

function QuestionInput({ q, value, onChange }: { q: PaperQuestion; value: any; onChange: (v: any) => void }) {
  switch (q.type) {
    case 'multiple_choice': {
      const chosen: (number | string)[] = value?.choice ?? [];
      const toggle = (idx: number | string) => {
        if (q.multipleAnswers) {
          const next = chosen.includes(idx) ? chosen.filter((c) => c !== idx) : [...chosen, idx];
          onChange({ choice: next });
        } else onChange({ choice: [idx] });
      };
      return (
        <>
        {q.multipleAnswers && <p className="q__hint">Choose all that apply.</p>}
        <ul className="choices">
          {(q.options ?? []).map((o) => (
            <li key={o.index}>
              <label className={chosen.includes(o.index) ? 'is-chosen' : ''}>
                <input type={q.multipleAnswers ? 'checkbox' : 'radio'} name={`q${q.id}`}
                  checked={chosen.includes(o.index)} onChange={() => toggle(o.index)} />
                <span>{o.text}</span>
              </label>
            </li>
          ))}
        </ul>
        </>
      );
    }

    case 'ordering': {
      const order: (number | string)[] = value?.order ?? (q.options ?? []).map((o) => o.index);
      const move = (from: number, to: number) => {
        if (to < 0 || to >= order.length) return;
        const next = order.slice();
        const [x] = next.splice(from, 1);
        next.splice(to, 0, x);
        onChange({ order: next });
      };
      const text = (idx: number | string) => q.options?.find((o) => o.index === idx)?.text ?? '';
      return (
        <ol className="ordering">
          {order.map((idx, pos) => (
            <li key={idx}>
              <span>{text(idx)}</span>
              <span className="ordering__moves">
                <button type="button" onClick={() => move(pos, pos - 1)} disabled={pos === 0} aria-label="Move up">↑</button>
                <button type="button" onClick={() => move(pos, pos + 1)} disabled={pos === order.length - 1} aria-label="Move down">↓</button>
              </span>
            </li>
          ))}
        </ol>
      );
    }

    case 'drag_and_drop': {
      const picks: (number | string)[] = value?.pairs ?? (q.left ?? []).map(() => -1);
      return (
        <ul className="matching">
          {(q.left ?? []).map((l, i) => (
            <li key={i}>
              <span>{l}</span>
              <select value={picks[i] ?? -1} onChange={(e) => {
                const next = picks.slice();
                next[i] = e.target.value === '-1' ? -1 : e.target.value;
                onChange({ pairs: next });
              }}>
                <option value={-1}>Choose…</option>
                {(q.right ?? []).map((r) => <option key={r.index} value={r.index}>{r.text}</option>)}
              </select>
            </li>
          ))}
        </ul>
      );
    }

    case 'fill_the_gap': {
      const gaps: string[] = value?.gaps ?? Array.from({ length: q.gapCount ?? 1 }, () => '');
      return (
        <div className="gaps">
          {gaps.map((g, i) => (
            <label key={i}>
              <span className="muted">Blank {i + 1}</span>
              <input value={g} onChange={(e) => {
                const next = gaps.slice(); next[i] = e.target.value; onChange({ gaps: next });
              }} />
            </label>
          ))}
        </div>
      );
    }

    case 'freetext':
      return <textarea className="freetext" rows={5} value={value?.text ?? ''}
        onChange={(e) => onChange({ text: e.target.value })} />;

    case 'likert_scale': {
      const ratings: Record<string, number> = value?.ratings ?? {};
      const scale = q.scale ?? ['1', '2', '3', '4', '5'];
      return (
        <table className="likert">
          <thead>
            <tr><th /> {scale.map((sv) => <th key={sv}>{sv}</th>)}</tr>
          </thead>
          <tbody>
            {(q.children ?? []).map((c) => (
              <tr key={c.index}>
                <th scope="row"><span dangerouslySetInnerHTML={{ __html: c.textHtml }} /></th>
                {scale.map((_, si) => (
                  <td key={si}>
                    <input type="radio" name={`q${q.id}c${c.index}`} checked={ratings[c.index] === si + 1}
                      onChange={() => onChange({ ratings: { ...ratings, [c.index]: si + 1 } })} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    default:
      return <p className="muted">This question type is not supported yet.</p>;
  }
}

function Results({ result, isSurvey, busy, onRefresh }: { result: SubmitResult; isSurvey: boolean; busy: boolean; onRefresh: () => void }) {
  const verdict = useMemo(() => {
    if (isSurvey) return { title: 'Thank you', tone: 'ok' as const, line: 'Your answers have been recorded.' };
    if (result.needsReview) return { title: 'Submitted', tone: 'wait' as const, line: 'An instructor will mark the written answers.' };
    return result.passed
      ? { title: 'Passed', tone: 'ok' as const, line: `${result.score}% — the pass mark is ${result.passMark}%.` }
      : { title: 'Not passed', tone: 'bad' as const, line: `${result.score}% — the pass mark is ${result.passMark}%.` };
  }, [result, isSurvey]);
  const canReview = result.review?.available !== false;

  return <div className={`verdict verdict--${verdict.tone}`}>
    <h2>{verdict.title}</h2>
    <p>{verdict.line}</p>
    {!isSurvey && <p className="muted">{result.earned} of {result.possible} points{result.needsReview ? ' so far; marking is pending' : ''}.</p>}
    {!isSurvey && !canReview && <p>{result.review?.reason ?? 'Question results are not available for this attempt.'}</p>}
    {!isSurvey && canReview && !!result.results?.length && <details className="review">
      <summary>Review question results</summary>
      <ol>
        {result.results.map((row, index) => <li key={row.questionId ?? index} style={{ marginBlock: 20 }}>
          <p><strong>Question {row.questionNumber ?? index + 1}</strong> · {row.correct == null ? 'Awaiting marking' : row.correct ? 'Correct' : 'Not correct'} · {row.points} point{row.points === 1 ? '' : 's'}</p>
          {row.textHtml && <div className="prose" dangerouslySetInnerHTML={{ __html: row.textHtml }} />}
          {result.review?.showFeedback !== false && row.feedbackHtml && <div className="prose" dangerouslySetInnerHTML={{ __html: row.feedbackHtml }} />}
          {result.review?.showFeedback !== false && row.comment && <p><strong>Instructor feedback:</strong> {row.comment}</p>}
          {result.review?.showCorrectAnswers !== false && !!row.correctAnswerText?.length && <>
            <p><strong>Correct answer{row.correctAnswerText.length === 1 ? '' : 's'}:</strong></p>
            <ul>{row.correctAnswerText.map((answer, answerIndex) => <li key={answerIndex}>{answer}</li>)}</ul>
          </>}
        </li>)}
      </ol>
    </details>}
    {!isSurvey && <button className="btn" disabled={busy} onClick={onRefresh}>{busy ? 'Loading…' : result.needsReview ? 'Refresh marks' : 'Refresh results'}</button>}
  </div>;
}
